<template>
  <div class="ai-assistant-panel">
    <div class="ai-panel-header">
      <div class="ai-status-dot"></div>
      <span class="ai-title">AI Assistant</span>
    </div>
    
    <div class="ai-panel-content">
      <div class="ai-chat-container">
        <div class="ai-chat-messages" ref="chatMessages">
          <div class="ai-message-wrapper" v-for="message in messages" :key="message.id">
            <div class="ai-message" :class="message.type === 'user' ? 'ai-message-user' : 'ai-message-assistant'">
              <div class="ai-message-content">
                <p v-html="formatMessage(message.content)"></p>
              </div>
              <div class="ai-message-timestamp" v-if="message.timestamp">
                {{ formatTimestamp(message.timestamp) }}
              </div>
            </div>
          </div>
          <!-- Loading indicator when AI is processing -->
          <div class="ai-message-wrapper" v-if="isProcessing">
            <div class="ai-message ai-message-assistant">
              <div class="ai-message-content ai-thinking">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="ai-input-container">
          <input 
            type="text" 
            v-model="userInput" 
            placeholder="Type your message..." 
            class="ai-input"
            @keyup.enter="sendMessage"
          />
          <label for="file-upload" class="ai-file-button" title="Upload file">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
              <polyline points="13 2 13 9 20 9"></polyline>
            </svg>
            <input 
              id="file-upload" 
              type="file" 
              @change="handleFileUpload" 
              accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.jpg,.jpeg,.png" 
              style="display: none;"
            />
          </label>
          <button @click="sendMessage" class="ai-send-button" :disabled="!userInput.trim()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="m22 2-7 20-4-9-9-4z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PdfService from '../services/PdfService';

export default {
  props: {
    supplyChainData: {
      type: Object,
      default: () => ({})
    },
    activeTab: {
      type: String,
      default: 'supply-chain'
    },
    farmDetails: {
      type: Object,
      default: () => ({})
    },
    factoryDetails: {
      type: Object,
      default: () => ({})
    },
    exportDetails: {
      type: Object,
      default: () => ({})
    },
    auctionDetails: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      userInput: '',
      messages: [],
      isProcessing: false,
      supplyChainContext: null,
      entityType: null,
      initialMessageSent: false,
      uploadedFile: null
    };
  },
  mounted() {
    console.log('AI Assistant: Component mounted');
    // Log the initial props received
    console.log('AI Assistant: Initial supplyChainData:', this.supplyChainData);
    console.log('AI Assistant: Initial activeTab:', this.activeTab);
    
    // Check if we have initial data and process it immediately
    if (this.supplyChainData && Object.keys(this.supplyChainData).length > 0) {
      console.log('AI Assistant: Processing initial data on mount');
      this.prepareSupplyChainContext(this.supplyChainData);
      this.determineEntityType(this.supplyChainData);
      
      // If we have a shipping company or other specific entity type, send initial message
      if (this.entityType && this.entityType !== 'unknown') {
        console.log('AI Assistant: Detected entity type on mount:', this.entityType);
        this.sendInitialMessage();
      }
    }
  },
  
  watch: {
    supplyChainData: {
      immediate: true,
      deep: true,
      handler(newData, oldData) {
        // Only process if this is new data or first load
        if (newData && Object.keys(newData).length > 0) {
          console.log('AI Assistant: Processing new supply chain data');
          console.log('AI Assistant: Data keys:', Object.keys(newData));
          console.log('AI Assistant: Data sample:', {
            code: newData.code,
            PIC: newData.PIC,
            FactoryID: newData.FactoryID,
            factory: newData.factory,
            BrokerName: newData.BrokerName,
            broker: newData.broker,
            PortID: newData.PortID,
            port: newData.port,
            TestCertID: newData.TestCertID,
            awta_certificate: newData.awta_certificate,
            LineID: newData.LineID,
            raw_data: newData.raw_data ? 'present' : 'absent'
          });
          
          // Check if data is nested in raw_data
          if (newData.raw_data) {
            console.log('AI Assistant: Raw data keys:', Object.keys(newData.raw_data));
            console.log('AI Assistant: Raw data sample:', {
              PIC: newData.raw_data.PIC,
              FactoryID: newData.raw_data.FactoryID,
              LineID: newData.raw_data.LineID
            });
          }
          
          this.prepareSupplyChainContext(newData);
          this.determineEntityType(newData);
          
          // Reset initialMessageSent flag when entity changes
          if (oldData && this.getEntityId(newData) !== this.getEntityId(oldData)) {
            console.log('AI Assistant: Entity changed, resetting messages');
            this.initialMessageSent = false;
            this.messages = [];
          }
          
          this.sendInitialMessage();
        } else {
          console.log('AI Assistant: Empty or unchanged data received');
        }
      }
    },
    messages: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    }
  },
  methods: {
    prepareSupplyChainContext(data) {
      console.log('AI Assistant: Preparing supply chain context with props:', {
        hasFarmDetails: Object.keys(this.farmDetails || {}).length > 0,
        hasFactoryDetails: Object.keys(this.factoryDetails || {}).length > 0,
        hasExportDetails: Object.keys(this.exportDetails || {}).length > 0,
        hasAuctionDetails: Object.keys(this.auctionDetails || {}).length > 0
      });
      
      // Extract relevant data from the supply chain data to provide context for the AI
      this.supplyChainContext = {
        farmDetails: {
          name: data.FarmName || data.origin_farm || this.farmDetails?.location?.split(',')[0],
          pic: data.PIC || this.farmDetails?.PIC,
          location: data.FarmLocation || data.Region || this.farmDetails?.location,
          sustainabilityScore: data.SustainabilityScore,
          owner: data.OwnerName || data.owner,
          state: data.State,
          lga: data.LGA || (this.farmDetails?.location ? this.farmDetails.location.split(',')[1]?.trim() : null)
        },
        woolDetails: {
          lineId: data.LineID || data.code,
          type: data.type || data.Description,
          micron: data.Micron_um || data.Micron,
          yield: data.Yield_pct || data.YieldPercent,
          stapleLength: data.StapleLength,
          stapleStrength: data.StapleStrength || data.StapleStrength_Nktex,
          mulesingStatus: data.MulesingStatus,
          description: data.description || data.Description || '',
          classer: data.ClasserName || data.classerInfo || '',
          classerId: data.ClasserID || ''
        },
        testingDetails: {
          testCertId: data.TestCertID || data.awta_certificate,
          testDate: data.TestDate,
          vmContent: data.VMContent || data.vm_content
        },
        auctionDetails: {
          lotNo: data.LotNo || data.LotNumber || this.auctionDetails?.lot_number,
          saleCenter: data.SaleCenter || this.auctionDetails?.sale_center,
          saleDate: data.SaleDateTime || data.SaleDate || this.auctionDetails?.sale_date,
          price: data.SalePrice_cents_per_kg_greasy || data.price,
          broker: data.BrokerName || data.broker || this.auctionDetails?.broker,
          mulesingStatus: data.MulesingStatus || this.auctionDetails?.mulesing_status
        },
        exportDetails: {
          consignmentId: data.ConsignmentID || data.export_consignment || this.exportDetails?.consignment_id,
          destination: data.DestinationCountry,
          portId: data.PortID || data.port_id || (this.exportDetails?.port ? this.exportDetails.port.split(' ')[0] : null),
          portName: data.PortName || data.port || this.exportDetails?.port,
          vesselName: data.VesselName || data.vessel,
          vesselImo: data.VesselIMO || data.vessel_imo,
          freightCompany: data.FreightCompany || data.freight_company || this.exportDetails?.freight_company,
          permit: this.exportDetails?.permit,
          containers: this.exportDetails?.containers
        },
        factoryDetails: {
          factoryId: data.FactoryID || this.factoryDetails?.factory_id,
          factoryName: data.FactoryName || data.factory || data.destination || this.factoryDetails?.name,
          country: data.FactoryCountry || data.Country || data.country || this.factoryDetails?.country,
          city: data.FactoryCity || data.City,
          buyer: data.FactoryBuyer || data.Buyer,
          certification: this.factoryDetails?.certification
        },
        productDetails: {
          brand: data.Brand || data.brand,
          fabricType: data.FabricType || data.fabric_type,
          fabricRollId: data.FabricRollID || data.fabric_roll,
          fabricWeight: data.FabricWeight || data.fabric_weight || data.gsm,
          yarnCount: data.YarnCount || data.yarn_count,
          yarnBatchId: data.YarnBatchID || data.yarn_batch
        },
        sustainabilityDetails: {
          carbonFootprint: data.CarbonFootprint_kg_CO2,
          waterUsage: data.WaterUsage_L,
          certifications: {
            rws: data.RWS_Certified,
            zq: data.ZQ_Certified,
            gots: data.GOTS_Certified,
            sustainaWool: data.SustainaWool
          }
        },
        riskDetails: {
          riskLevel: data.RiskLevel || data.risk_level,
          riskFactors: data.RiskFactors || data.risk_factors,
          dataQuality: data.DataQuality || data.data_quality
        }
      };
    },
    
    getEntityId(data) {
      // Get a unique identifier for the entity to track changes
      // First check direct properties
      if (data.PIC) return `farm-${data.PIC}`;
      if (data.FarmName) return `farm-${data.FarmName}`;
      if (data.FactoryID) return `factory-${data.FactoryID}`;
      if (data.factory) return `factory-${data.factory}`;
      if (data.BrokerName) return `broker-${data.BrokerName}`;
      if (data.broker) return `broker-${data.broker}`;
      if (data.PortID) return `port-${data.PortID}`;
      if (data.port) return `port-${data.port}`;
      if (data.TestCertID) return `test-${data.TestCertID}`;
      if (data.awta_certificate) return `test-${data.awta_certificate}`;
      if (data.LineID) return `line-${data.LineID}`;
      if (data.code && data.code.match(/^[A-Z]+-\d+-\d+$/)) return `line-${data.code}`;
      
      // Then check if data is nested in raw_data
      if (data.raw_data) {
        const raw = data.raw_data;
        if (raw.PIC) return `farm-${raw.PIC}`;
        if (raw.FarmName) return `farm-${raw.FarmName}`;
        if (raw.FactoryID) return `factory-${raw.FactoryID}`;
        if (raw.factory) return `factory-${raw.factory}`;
        if (raw.BrokerName) return `broker-${raw.BrokerName}`;
        if (raw.broker) return `broker-${raw.broker}`;
        if (raw.PortID) return `port-${raw.PortID}`;
        if (raw.port) return `port-${raw.port}`;
        if (raw.TestCertID) return `test-${raw.TestCertID}`;
        if (raw.awta_certificate) return `test-${raw.awta_certificate}`;
        if (raw.LineID) return `line-${raw.LineID}`;
        if (raw.code && raw.code.match(/^[A-Z]+-\d+-\d+$/)) return `line-${raw.code}`;
      }
      
      // Use type as a fallback
      if (data.type === 'factory') return `factory-${data.destination || 'unknown'}`;
      if (data.type === 'auction') return `auction-${data.destination || 'unknown'}`;
      if (data.type === 'port') return `port-${data.destination || 'unknown'}`;
      if (data.type === 'broker') return `broker-${data.destination || 'unknown'}`;
      if (data.type === 'testing') return `testing-${data.destination || 'unknown'}`;
      if (data.type === 'shipping_company') return `shipping-${data.name || data.code || 'unknown'}`;
      if (data.type === 'shipping') return `shipping-${data.name || data.code || 'unknown'}`;
      if (data.type === 'brand') return `brand-${data.name || data.code || 'unknown'}`;
      if (data.type === 'government_agency') return `gov-${data.name || data.code || 'unknown'}`;
      if (data.type === 'auction_house') return `auction-${data.name || data.code || 'unknown'}`;
      if (data.type === 'certification_body') return `cert-${data.name || data.code || 'unknown'}`;
      if (data.type === 'testing_lab') return `lab-${data.name || data.code || 'unknown'}`;
      if (data.type === 'classer') return `classer-${data.name || data.code || 'unknown'}`;
      
      // Check for shipping company by name
      if (data.name && (data.name.toLowerCase().includes('freight') || 
                       data.name.toLowerCase().includes('shipping') || 
                       data.name.toLowerCase().includes('logistics'))) {
        return `shipping-${data.name}`;
      }
      
      // Check for shipping company by ID
      if (data.id && typeof data.id === 'string' && data.id.toLowerCase().includes('shipping')) {
        return `shipping-${data.id}`;
      }
      
      // Last resort - use a hash of the data
      return `entity-${JSON.stringify(data).slice(0, 50)}`;
    },
    
    determineEntityType(data) {
      // Determine if this is a farm, commercial entity, or wool line
      // Log the data we're examining to help with debugging
      console.log('AI Assistant: Determining entity type for:', {
        hasPIC: !!data.PIC,
        hasFarmName: !!data.FarmName,
        hasLineID: !!data.LineID,
        hasFactoryID: !!data.FactoryID,
        hasFactory: !!data.factory,
        hasBroker: !!(data.BrokerName || data.broker),
        hasPort: !!(data.PortID || data.port),
        hasTest: !!(data.TestCertID || data.awta_certificate),
        hasFreightCompany: !!(data.FreightCompany || data.freight_company),
        hasVessel: !!(data.VesselName || data.vessel),
        code: data.code,
        type: data.type,
        destination: data.destination
      });
      
      // Log additional props
      console.log('AI Assistant: Additional props for entity detection:', {
        hasFarmDetailsProps: Object.keys(this.farmDetails || {}).length > 0,
        hasFactoryDetailsProps: Object.keys(this.factoryDetails || {}).length > 0,
        hasExportDetailsProps: Object.keys(this.exportDetails || {}).length > 0,
        hasAuctionDetailsProps: Object.keys(this.auctionDetails || {}).length > 0
      });
      
      // Check if data is nested in raw_data
      let rawData = null;
      if (data.raw_data) {
        rawData = data.raw_data;
        console.log('AI Assistant: Raw data entity indicators:', {
          hasPIC: !!rawData.PIC,
          hasFarmName: !!rawData.FarmName,
          hasLineID: !!rawData.LineID,
          hasFactoryID: !!rawData.FactoryID,
          hasFactory: !!rawData.factory,
          hasBroker: !!(rawData.BrokerName || rawData.broker),
          hasPort: !!(rawData.PortID || rawData.port),
          hasTest: !!(rawData.TestCertID || rawData.awta_certificate)
        });
      }
      
      // Check for farm indicators
      if (data.PIC || (data.FarmName && !data.LineID) || 
          (rawData && (rawData.PIC || (rawData.FarmName && !rawData.LineID))) ||
          Object.keys(this.farmDetails || {}).length > 0) {
        this.entityType = 'farm';
        console.log('AI Assistant: Detected entity type - farm');
        return;
      }
      
      // Check for shipping company indicators first (special case of commercial entity)
      if (data.FreightCompany || data.freight_company || 
          (data.type && (data.type.toLowerCase() === 'shipping' || data.type.toLowerCase() === 'shipping_company')) ||
          (data.name && data.name.toLowerCase().includes('freight')) ||
          (data.name && data.name.toLowerCase().includes('shipping')) ||
          (data.name && data.name.toLowerCase().includes('logistics')) ||
          (data.id && typeof data.id === 'string' && data.id.toLowerCase().includes('shipping')) ||
          (rawData && (rawData.FreightCompany || rawData.freight_company))) {
        this.entityType = 'shipping';
        console.log('AI Assistant: Detected entity type - shipping company from', 
                   data.type || data.name || data.id);
        return;
      }
      
      // Check for commercial entity indicators
      if (data.FactoryID || data.factory || data.BrokerName || data.broker || 
          data.PortID || data.port || data.TestCertID || data.awta_certificate ||
          (rawData && (rawData.FactoryID || rawData.factory || rawData.BrokerName || 
                      rawData.broker || rawData.PortID || rawData.port || 
                      rawData.TestCertID || rawData.awta_certificate)) ||
          Object.keys(this.factoryDetails || {}).length > 0 ||
          Object.keys(this.exportDetails || {}).length > 0) {
        this.entityType = 'commercial';
        console.log('AI Assistant: Detected entity type - commercial');
        return;
      }
      
      // Check for wool line indicators
      if (data.LineID || (data.code && data.code.match(/^[A-Z]+-\d+-\d+$/)) ||
          (rawData && (rawData.LineID || (rawData.code && rawData.code.match(/^[A-Z]+-\d+-\d+$/))))) {
        this.entityType = 'woolLine';
        console.log('AI Assistant: Detected entity type - wool line');
        return;
      }
      
      // Check for specific commercial entity types
      if (data.type) {
        switch(data.type.toLowerCase()) {
          case 'brand':
            this.entityType = 'brand';
            console.log('AI Assistant: Detected entity type - brand');
            return;
          case 'government_agency':
            this.entityType = 'government_agency';
            console.log('AI Assistant: Detected entity type - government agency');
            return;
          case 'auction_house':
            this.entityType = 'auction_house';
            console.log('AI Assistant: Detected entity type - auction house');
            return;
          case 'certification_body':
            this.entityType = 'certification_body';
            console.log('AI Assistant: Detected entity type - certification body');
            return;
          case 'testing_lab':
            this.entityType = 'testing_lab';
            console.log('AI Assistant: Detected entity type - testing lab');
            return;
          case 'classer':
            this.entityType = 'classer';
            console.log('AI Assistant: Detected entity type - classer');
            return;
          case 'factory':
          case 'auction':
          case 'port':
          case 'broker':
          case 'testing':
            this.entityType = 'commercial';
            console.log('AI Assistant: Detected commercial entity from type:', data.type);
            return;
        }
      }
      
      // Check for specific entities by name or code
      if (data.name) {
        const name = data.name.toLowerCase();
        if (name.includes('uniqlo') || name.includes('icebreaker')) {
          this.entityType = 'brand';
          console.log('AI Assistant: Detected entity type - brand from name:', data.name);
          return;
        }
        if (name.includes('wool exchange') || name.includes('awex')) {
          this.entityType = 'government_agency';
          console.log('AI Assistant: Detected entity type - government agency from name:', data.name);
          return;
        }
        if (name.includes('auction center') || name.includes('auction house')) {
          this.entityType = 'auction_house';
          console.log('AI Assistant: Detected entity type - auction house from name:', data.name);
          return;
        }
        if (name.includes('sustainawool') || name.includes('integrity scheme')) {
          this.entityType = 'certification_body';
          console.log('AI Assistant: Detected entity type - certification body from name:', data.name);
          return;
        }
        if (name.includes('testing authority') || name.includes('awta')) {
          this.entityType = 'testing_lab';
          console.log('AI Assistant: Detected entity type - testing lab from name:', data.name);
          return;
        }
        if ((name.includes('jordan') && name.includes('blake')) || 
            (name.includes('taylor') && name.includes('quinn'))) {
          this.entityType = 'classer';
          console.log('AI Assistant: Detected entity type - classer from name:', data.name);
          return;
        }
      }
      
      // Check by ID
      if (data.id && typeof data.id === 'string') {
        const id = data.id.toLowerCase();
        if (id.includes('brand-')) {
          this.entityType = 'brand';
          console.log('AI Assistant: Detected entity type - brand from id:', data.id);
          return;
        }
        if (id.includes('gov-')) {
          this.entityType = 'government_agency';
          console.log('AI Assistant: Detected entity type - government agency from id:', data.id);
          return;
        }
        if (id.includes('auction-')) {
          this.entityType = 'auction_house';
          console.log('AI Assistant: Detected entity type - auction house from id:', data.id);
          return;
        }
        if (id.includes('cert-')) {
          this.entityType = 'certification_body';
          console.log('AI Assistant: Detected entity type - certification body from id:', data.id);
          return;
        }
        if (id.includes('lab-')) {
          this.entityType = 'testing_lab';
          console.log('AI Assistant: Detected entity type - testing lab from id:', data.id);
          return;
        }
        if (id.includes('classer-')) {
          this.entityType = 'classer';
          console.log('AI Assistant: Detected entity type - classer from id:', data.id);
          return;
        }
      }
      
      // Additional heuristics for commercial entities
      if ((data.destination && typeof data.destination === 'string' && 
           !data.destination.toLowerCase().includes('farm'))) {
        this.entityType = 'commercial';
        console.log('AI Assistant: Detected commercial entity from destination');
        return;
      }
      
      // Check if we have auction details
      if (Object.keys(this.auctionDetails || {}).length > 0) {
        this.entityType = 'commercial';
        console.log('AI Assistant: Detected commercial entity from auction details');
        return;
      }
      
      // Default to unknown if no indicators match
      this.entityType = 'unknown';
      console.log('AI Assistant: Detected entity type - unknown');
    },
    
    sendInitialMessage() {
      // Only send initial message once when data is first loaded
      if (this.initialMessageSent || !this.entityType) {
        console.log('AI Assistant: Skipping initial message', { initialMessageSent: this.initialMessageSent, entityType: this.entityType });
        return;
      }
      
      console.log('AI Assistant: Sending initial welcome message for entity type:', this.entityType);
      this.isProcessing = true;
      
      // Set flag immediately to prevent duplicate calls
      this.initialMessageSent = true;
      
      // Clear any existing messages
      this.messages = [];
      
      // Generate welcome message based on entity type
      const welcomeMessage = this.generateWelcomeMessage();
      
      const aiMessage = {
        id: Date.now(),
        type: 'assistant',
        content: welcomeMessage,
        timestamp: new Date()
      };
      
      // Add message after a short delay to simulate typing
      setTimeout(() => {
        this.messages.push(aiMessage);
        this.isProcessing = false;
        // We no longer automatically send the entity insight
        // The user will need to ask for this information
      }, 300);
    },
    
    sendEntityInsight() {
      console.log('AI Assistant: Sending entity insight for entity type:', this.entityType);
      this.isProcessing = true;
      
      // Generate insight based on entity type
      const insightMessage = this.generateEntityInsight();
      
      // Only send if we have a meaningful insight
      if (insightMessage && insightMessage.trim() !== '') {
        const aiMessage = {
          id: Date.now(),
          type: 'assistant',
          content: insightMessage,
          timestamp: new Date()
        };
        
        // Add message after a delay to simulate typing
        setTimeout(() => {
          this.messages.push(aiMessage);
          this.isProcessing = false;
        }, 800);
      } else {
        console.log('AI Assistant: No meaningful insight to send');
        this.isProcessing = false;
      }
    },
    
    generateWelcomeMessage() {
      console.log('AI Assistant: Generating welcome message for entity type:', this.entityType);
      console.log('AI Assistant: Entity data:', {
        name: this.supplyChainData?.name,
        type: this.supplyChainData?.type,
        id: this.supplyChainData?.id
      });
      
      switch(this.entityType) {
        case 'brand':
          const brandName = this.supplyChainData?.name || 'this brand';
          return `Welcome to ${brandName}'s detail view. I can provide information about this brand, its wool products, sustainability practices, and connections to farms and manufacturers in the supply chain. What would you like to know?`;
          
        case 'shipping':
          // Get the shipping company name from various possible sources
          const freightCompany = this.supplyChainContext?.exportDetails?.freightCompany || 
                               this.supplyChainData?.name || 
                               'this shipping company';
          return `Welcome to ${freightCompany}'s detail view. I can provide information about this shipping company, the wool consignments it handles, vessel details, and its role in the export process. What would you like to know?`;
          
        case 'farm':
          const farmName = this.supplyChainContext?.farmDetails?.name || 
                         this.supplyChainData?.name || 
                         'this farm';
          return `Welcome to ${farmName}'s detail view. I can provide information about this farm, its wool production, sustainability practices, and connections to other entities in the supply chain. What would you like to know?`;
        
        case 'commercial':
          // Determine what kind of commercial entity we're looking at
          let entityName = '';
          let entityType = '';
          
          if (this.supplyChainContext?.factoryDetails?.factoryName) {
            entityName = this.supplyChainContext.factoryDetails.factoryName;
            entityType = 'processing facility';
          } else if (this.supplyChainContext?.auctionDetails?.saleCenter) {
            entityName = this.supplyChainContext.auctionDetails.saleCenter;
            entityType = 'auction center';
          } else if (this.supplyChainContext?.exportDetails?.portName) {
            entityName = this.supplyChainContext.exportDetails.portName;
            entityType = 'port facility';
          } else if (this.supplyChainContext?.testingDetails?.testCertId) {
            entityName = `AWTA ${this.supplyChainContext.testingDetails.testCertId}`;
            entityType = 'testing certificate';
          }
          
          // If we have a specific entity name, use it
          if (entityName) {
            return `Welcome to ${entityName}'s ${entityType} detail view. I can provide information about this commercial entity, its role in the wool supply chain, and its connections to farms, processors, and other entities. What would you like to know?`;
          }
          
          // Check if we can determine the type from the data
          if (this.supplyChainData?.type) {
            switch(this.supplyChainData.type.toLowerCase()) {
              case 'factory':
                const factoryName = this.supplyChainData?.name || 'this factory';
                return `Welcome to ${factoryName}'s detail view. I can provide information about this processing facility, the wool it handles, and its connections to farms and other entities in the supply chain. What would you like to know?`;
                
              case 'auction':
              case 'auction_house':
                const auctionName = this.supplyChainData?.name || 'this auction center';
                return `Welcome to ${auctionName}'s detail view. I can provide information about this auction facility, the wool lots sold here, and connections to farms and buyers. What would you like to know?`;
                
              case 'port':
                const portName = this.supplyChainData?.name || 'this port facility';
                return `Welcome to ${portName}'s detail view. I can provide information about this export point, shipping details, and the wool being transported through it. What would you like to know?`;
                
              case 'broker':
                const brokerName = this.supplyChainData?.name || 'this wool broker';
                return `Welcome to ${brokerName}'s detail view. I can provide information about this broker's role in connecting farms to buyers, auction activities, and wool lot details. What would you like to know?`;
                
              case 'testing':
              case 'testing_lab':
                const testingName = this.supplyChainData?.name || 'this testing facility';
                return `Welcome to ${testingName}'s detail view. I can provide information about these test results, quality metrics, and how they relate to farms and buyers in the supply chain. What would you like to know?`;
                
              case 'brand':
                const brandName = this.supplyChainData?.name || 'this brand';
                return `Welcome to ${brandName}'s detail view. I can provide information about this brand, its wool products, sustainability practices, and connections to farms and manufacturers in the supply chain. What would you like to know?`;
                
              case 'government_agency':
                const agencyName = this.supplyChainData?.name || 'this government agency';
                return `Welcome to ${agencyName}'s detail view. I can provide information about this agency's role in regulating and supporting the wool industry, standards enforcement, and market reporting. What would you like to know?`;
                
              case 'certification_body':
                const certBodyName = this.supplyChainData?.name || 'this certification body';
                return `Welcome to ${certBodyName}'s detail view. I can provide information about this certification scheme, its standards, participating farms, and how it ensures sustainability and ethical practices in wool production. What would you like to know?`;
                
              case 'classer':
                const classerName = this.supplyChainData?.name || 'this wool classer';
                return `Welcome to ${classerName}'s detail view. I can provide information about this wool classer, their role in grading and categorizing wool, and the farms and wool lines they've worked with. What would you like to know?`;
            }
          }
          
          // Generic commercial entity message if we can't determine specifics
          const genericName = this.supplyChainData?.name || 'this commercial entity';
          return `Welcome to ${genericName}'s detail view. I can provide information about this entity's role in the wool supply chain, its connections to farms and other businesses, and the wool flowing through it. What would you like to know?`;
        
        case 'woolLine':
          const lineId = this.supplyChainContext?.woolDetails?.lineId || 
                       this.supplyChainData?.code || 
                       'this wool line';
          return `Welcome to ${lineId}'s detail view. I can provide information about this wool line, its quality metrics, processing journey, and connections to farms and commercial entities. What would you like to know?`;
        
        case 'government_agency':
          const agencyName = this.supplyChainData?.name || 'this government agency';
          return `Welcome to ${agencyName}'s detail view. I can provide information about this agency's role in regulating and supporting the wool industry, standards enforcement, and market reporting. What would you like to know?`;
        
        case 'certification_body':
          const certBodyName = this.supplyChainData?.name || 'this certification body';
          return `Welcome to ${certBodyName}'s detail view. I can provide information about this certification scheme, its standards, participating farms, and how it ensures sustainability and ethical practices in wool production. What would you like to know?`;
        
        case 'classer':
          const classerName = this.supplyChainData?.name || 'this wool classer';
          return `Welcome to ${classerName}'s detail view. I can provide information about this wool classer, their role in grading and categorizing wool, and the farms and wool lines they've worked with. What would you like to know?`;
        
        case 'testing_lab':
          const labName = this.supplyChainData?.name || 'this testing laboratory';
          return `Welcome to ${labName}'s detail view. I can provide information about these test results, quality metrics, and how they relate to farms and buyers in the supply chain. What would you like to know?`;
        
        case 'auction_house':
          const auctionHouseName = this.supplyChainData?.name || 'this auction house';
          return `Welcome to ${auctionHouseName}'s detail view. I can provide information about this auction facility, the wool lots sold here, and connections to farms and buyers. What would you like to know?`;
        
        default:
          return `Welcome to the supply chain view. I can help you understand the wool supply chain data and answer questions about traceability. What would you like to know?`;
      }
    },
    
    generateEntityInsight() {
      switch(this.entityType) {
        case 'brand':
          const brandName = this.supplyChainData?.name || 'This brand';
          let brandInsight = `${brandName} is a wool product brand in the global textile market. `;
          
          if (this.supplyChainContext?.productDetails?.fabricType) {
            brandInsight += `They specialize in ${this.supplyChainContext.productDetails.fabricType} products. `;
          }
          
          brandInsight += `Their wool products are sourced from Australian farms and processed through a traceable supply chain that ensures quality and sustainability. `;
          
          if (this.supplyChainContext?.sustainabilityDetails?.certifications) {
            const certs = this.supplyChainContext.sustainabilityDetails.certifications;
            if (certs.rws || certs.zq || certs.gots || certs.sustainaWool) {
              brandInsight += `They maintain certifications including ${certs.rws ? 'RWS, ' : ''}${certs.zq ? 'ZQ, ' : ''}${certs.gots ? 'GOTS, ' : ''}${certs.sustainaWool ? 'SustainaWool' : ''}.`;
            }
          }
          
          return brandInsight;

        case 'government_agency':
          const agencyName = this.supplyChainData?.name || 'This government agency';
          let agencyInsight = `${agencyName} is a regulatory body in the wool industry. `;
          
          if (this.supplyChainData?.jurisdiction) {
            agencyInsight += `They operate within the ${this.supplyChainData.jurisdiction} jurisdiction. `;
          }
          
          agencyInsight += `They establish standards, provide market reporting, and ensure compliance across the wool supply chain. Their work helps maintain Australia's reputation for high-quality wool exports.`;
          
          return agencyInsight;

        case 'certification_body':
          const certBodyName = this.supplyChainData?.name || 'This certification body';
          let certInsight = `${certBodyName} provides certification standards for the wool industry. `;
          
          if (this.supplyChainData?.standardsenforced) {
            certInsight += `They enforce standards related to ${this.supplyChainData.standardsenforced}. `;
          }
          
          certInsight += `Their certification ensures that wool is produced according to specific quality, sustainability, and ethical standards. Farms and processors that meet these standards can use the certification to demonstrate their commitment to responsible practices.`;
          
          return certInsight;

        case 'classer':
          const classerName = this.supplyChainData?.name || 'This wool classer';
          let classerInsight = `${classerName} is a registered wool classer `;
          
          if (this.supplyChainData?.awexregistration) {
            classerInsight += `with AWEX registration ${this.supplyChainData.awexregistration}. `;
          } else {
            classerInsight += `. `;
          }
          
          classerInsight += `Wool classers play a critical role in the wool supply chain by assessing, grading, and categorizing wool based on quality characteristics like fiber diameter, length, strength, and color. Their expertise ensures that wool is properly sorted for optimal processing and market value.`;
          
          return classerInsight;

        case 'testing_lab':
          const labName = this.supplyChainData?.name || 'This testing laboratory';
          let labInsight = `${labName} provides wool testing and certification services. `;
          
          if (this.supplyChainData?.accreditationlevel) {
            labInsight += `They maintain ${this.supplyChainData.accreditationlevel} accreditation. `;
          }
          
          labInsight += `Testing labs analyze wool samples to determine key quality metrics including micron (fiber diameter), yield, staple length, staple strength, and vegetable matter content. These objective measurements are essential for determining wool value and processing suitability.`;
          
          return labInsight;

        case 'auction_house':
          const auctionName = this.supplyChainData?.name || 'This auction house';
          let auctionInsight = `${auctionName} is a wool auction center where buyers and sellers trade wool lots. `;
          
          if (this.supplyChainData?.annualsales) {
            auctionInsight += `They handle approximately ${this.supplyChainData.annualsales} sales annually. `;
          }
          
          auctionInsight += `Auction houses are central to the wool market, establishing transparent pricing and connecting wool producers with processors and exporters. They provide a competitive marketplace where wool lots are presented with their test certificates and quality details.`;
          
          return auctionInsight;
          
        case 'shipping':
          const export_info = this.supplyChainContext?.exportDetails;
          let shippingInsight = '';
          
          // Get the shipping company name from various possible sources
          const companyName = export_info?.freightCompany || 
                            this.supplyChainData?.name || 
                            'This shipping company';
          
          if (companyName) {
            shippingInsight = `${companyName} handles wool exports from Australia to international markets. `;
            
            if (export_info.vesselName) {
              shippingInsight += `They operate vessels such as ${export_info.vesselName}${export_info.vesselImo ? ` (IMO: ${export_info.vesselImo})` : ''}. `;
            }
            
            if (export_info.portName) {
              shippingInsight += `They operate through ${export_info.portName} and other major Australian ports. `;
            }
            
            if (export_info.consignmentId) {
              shippingInsight += `\n\nCurrent consignment ID: ${export_info.consignmentId}. `;
            }
            
            if (export_info.containers) {
              shippingInsight += `Container IDs: ${export_info.containers}. `;
            }
            
            shippingInsight += `\n\nThis company is responsible for ensuring wool shipments reach their international destinations safely and on schedule.`;
          } else {
            // Use the company name in the fallback message too
            shippingInsight = `${companyName} handles wool exports from Australia to international markets. They manage logistics, customs clearance, and shipping documentation to ensure smooth international trade of wool products.`;
          }
          
          return shippingInsight;
          
        case 'farm':
          const farm = this.supplyChainContext?.farmDetails;
          const wool = this.supplyChainContext?.woolDetails;
          let insight = '';
          
          if (farm && farm.name) {
            insight = `${farm.name} is located in ${farm.location || farm.state || 'Australia'}${farm.lga ? `, ${farm.lga} region` : ''}. `;
            
            if (farm.pic) {
              insight += `The farm's Property Identification Code (PIC) is ${farm.pic}. `;
            }
            
            if (this.supplyChainContext?.sustainabilityDetails?.certifications?.sustainaWool) {
              insight += `This farm is SustainaWool certified, indicating strong sustainability practices. `;
            }
            
            if (wool && wool.lineId) {
              insight += `\n\nRecent wool production includes line ${wool.lineId}`;
              if (wool.micron) {
                insight += ` with a micron measurement of ${wool.micron}μm`;
              }
              insight += '.';
            }
          }
          
          return insight || "I have information about this farm's wool production and supply chain connections. What specific details would you like to know?";
        
        case 'commercial':
          // Determine which type of commercial entity we're dealing with
          if (this.supplyChainContext?.factoryDetails?.factoryName) {
            const factory = this.supplyChainContext.factoryDetails;
            return `${factory.factoryName} is a processing facility located in ${factory.country || 'overseas'}${factory.city ? `, ${factory.city}` : ''}. This facility processes wool from multiple Australian farms and converts it into ${this.supplyChainContext?.productDetails?.fabricType || 'textile products'}.`;
          } 
          
          if (this.supplyChainContext?.auctionDetails?.saleCenter) {
            const auction = this.supplyChainContext.auctionDetails;
            return `${auction.saleCenter} is an auction center that handles wool sales. ${auction.broker ? `The broker ${auction.broker} facilitates these sales` : 'Multiple brokers facilitate these sales'} between wool producers and buyers.`;
          }
          
          if (this.supplyChainContext?.exportDetails?.portName) {
            const export_info = this.supplyChainContext.exportDetails;
            return `${export_info.portName} handles wool exports from Australia to international destinations. ${export_info.freightCompany ? `${export_info.freightCompany} provides shipping services` : 'Multiple shipping companies operate'} through this port.`;
          }
          
          if (this.supplyChainContext?.testingDetails?.testCertId) {
            return `AWTA (Australian Wool Testing Authority) provides independent wool testing and certification services. These test results are critical for determining wool value and quality throughout the supply chain.`;
          }
          
          // Check if we can determine the type from the data
          if (this.supplyChainData?.type) {
            switch(this.supplyChainData.type.toLowerCase()) {
              case 'factory':
                return `This processing facility converts raw wool into various textile products. It sources wool from multiple Australian farms and supplies to international brands and retailers. The facility implements quality control measures throughout the manufacturing process.`;
              
              case 'auction':
                return `This auction center is where wool lots from farms are sold to buyers and processors. Auctions play a critical role in the wool supply chain by establishing market prices and connecting producers with manufacturers.`;
              
              case 'port':
                return `This port facility handles the export of wool from Australia to international destinations. It manages logistics, customs clearance, and shipping documentation to ensure smooth international trade of wool products.`;
              
              case 'broker':
                return `Wool brokers play a vital intermediary role in the supply chain, connecting farmers with buyers and managing the sale process. They provide market expertise, quality assessment, and facilitate transactions between producers and processors.`;
              
              case 'testing':
                return `Wool testing is a critical quality control step in the supply chain. Testing facilities like AWTA measure key characteristics such as micron, yield, staple strength, and vegetable matter content, which determine wool value and processing suitability.`;
            }
          }
          
          // If we have a destination, use that
          if (this.supplyChainData?.destination) {
            return `This commercial entity (${this.supplyChainData.destination}) is part of the wool supply chain network. It handles wool as it moves from Australian farms to international markets, ensuring quality and traceability throughout the process.`;
          }
          
          return "This commercial entity plays an important role in the wool supply chain. It connects farms, processors, and retailers in the global wool industry. You can ask about its specific function, connections to farms, or role in ensuring wool quality and traceability.";
          
        
        case 'woolLine':
          const woolLine = this.supplyChainContext?.woolDetails;
          let woolInsight = '';
          
          if (woolLine && woolLine.lineId) {
            woolInsight = `Wool line ${woolLine.lineId} is ${woolLine.type || 'wool'}`;
            
            if (woolLine.micron) {
              woolInsight += ` with a micron measurement of ${woolLine.micron}μm`;
            }
            
            if (woolLine.yield) {
              woolInsight += ` and a yield of ${woolLine.yield}%`;
            }
            
            woolInsight += '.';
            
            if (woolLine.mulesingStatus) {
              woolInsight += ` Mulesing status: ${woolLine.mulesingStatus}.`;
            }
            
            if (this.supplyChainContext?.farmDetails?.name) {
              woolInsight += `\n\nThis wool originated from ${this.supplyChainContext.farmDetails.name} farm.`;
            }
            
            if (this.supplyChainContext?.auctionDetails?.saleCenter) {
              woolInsight += ` It was sold at ${this.supplyChainContext.auctionDetails.saleCenter}${this.supplyChainContext.auctionDetails.price ? ` for ${this.supplyChainContext.auctionDetails.price} cents per kg greasy` : ''}.`;
            }
          }
          
          return woolInsight || "I have information about this wool line's characteristics and journey through the supply chain. What specific details would you like to know?";
        
        default:
          return "I can help you understand the wool supply chain data. You can ask about farms, wool quality, processing, sustainability, or any other aspect of the supply chain.";
      }
    },
    
    sendMessage() {
      if ((!this.userInput.trim() && !this.uploadedFile) || this.isProcessing) return;
      
      let messageContent = this.userInput.trim();
      
      // If there's an uploaded file, add file information to the message
      if (this.uploadedFile) {
        if (messageContent) {
          messageContent += '\n\n';
        }
        messageContent += `[Attached file: ${this.uploadedFile.name}]`;
      }
      
      const userMessage = {
        id: Date.now(),
        type: 'user',
        content: messageContent,
        timestamp: new Date(),
        file: this.uploadedFile
      };
      
      this.messages.push(userMessage);
      const userMessageContent = messageContent;
      
      // Clear the input field and uploaded file
      this.userInput = '';
      const fileToProcess = this.uploadedFile;
      this.uploadedFile = null;
      this.isProcessing = true;
      
      // Store the user query for processing and convert to lowercase for checks
      const userQuery = userMessageContent;
      const lowerQuery = userQuery.toLowerCase();
      
      // Check if this is a file upload with audit report
      if (fileToProcess && lowerQuery.includes('audit')) {
        try {
          // Process the audit report file
          this.processAuditReport(fileToProcess);
          return;
        } catch (error) {
          console.error('Error processing audit report:', error);
          // Continue with normal response if there's an error
        }
      }
      
      // Check if this is a carbon reporting request
      if ((lowerQuery.includes('carbon') || lowerQuery.includes('sustainability')) && 
          (lowerQuery.includes('report') || lowerQuery.includes('reporting') || lowerQuery.includes('pdf') || lowerQuery.includes('status')) && 
          this.entityType === 'farm') {
        
        // Get the farm PIC
        const farmPIC = this.supplyChainContext?.farmDetails?.PIC || this.supplyChainData?.PIC;
        
        if (farmPIC) {
          try {
            // Set processing state to show the typing indicator
            this.isProcessing = true;
            
            // Handle the carbon report request directly without showing a loading message
            // We'll wait for it to complete before continuing
            this.handleCarbonReportRequest(farmPIC);
            return;
          } catch (error) {
            console.error('Error handling carbon report request:', error);
            // Continue with normal response if there's an error
          }
        }
      }
      
      // Process the user's message and generate a response
      setTimeout(() => {
        const response = this.generateAIResponse(userQuery);
        
        const aiMessage = {
          id: Date.now(),
          type: 'assistant',
          content: response,
          timestamp: new Date()
        };
        
        this.messages.push(aiMessage);
        this.isProcessing = false;
      }, 500);
    },
    
    generateAIResponse(query) {
      // Enhanced AI response generation based on the query and context
      const lowerQuery = query.toLowerCase();
      console.log('AI Assistant: Generating response for query:', query);
      console.log('AI Assistant: Current entity type:', this.entityType);
      
      // Check what we're looking at - if it's a general question about what's on screen
      if (lowerQuery.includes('what') && 
          (lowerQuery.includes('looking at') || lowerQuery.includes('seeing') || 
           lowerQuery.includes('is this') || lowerQuery.includes('am i viewing'))) {
        
        switch(this.entityType) {
          case 'farm':
            const farm = this.supplyChainContext?.farmDetails;
            return `You're viewing details for ${farm?.name || 'a farm'} located in ${farm?.location || farm?.state || 'Australia'}. This view shows the farm's connections in the wool supply chain, including wool production, processing, and distribution.`;
          
          case 'commercial':
            let entityDesc = '';
            if (this.supplyChainContext?.factoryDetails?.factoryName) {
              entityDesc = `${this.supplyChainContext.factoryDetails.factoryName} processing facility`;
            } else if (this.supplyChainContext?.auctionDetails?.saleCenter) {
              entityDesc = `${this.supplyChainContext.auctionDetails.saleCenter} auction center`;
            } else if (this.supplyChainContext?.exportDetails?.portName) {
              entityDesc = `${this.supplyChainContext.exportDetails.portName} port facility`;
            } else if (this.supplyChainContext?.testingDetails?.testCertId) {
              entityDesc = `AWTA testing details`;
            } else {
              entityDesc = 'a commercial entity in the wool supply chain';
            }
            return `You're viewing details for ${entityDesc}. This view shows how this entity connects to farms, wool processing, and other parts of the supply chain.`;
          
          case 'woolLine':
            const woolLine = this.supplyChainContext?.woolDetails;
            return `You're viewing details for wool line ${woolLine?.lineId || 'a wool batch'} from ${this.supplyChainContext?.farmDetails?.name || 'a farm'} to ${this.supplyChainContext?.factoryDetails?.factoryName || 'processing'}. This shows the complete journey of this specific wool batch through the supply chain.`;
          
          default:
            return `You're viewing the wool supply chain visualization. This shows the connections between farms, testing facilities, auctions, shipping, and manufacturing in the wool industry.`;
        }
      }
      
      // Mulesing status queries
      if (lowerQuery.includes('mulesing')) {
        // Check if we're looking at a farm
        if (this.entityType === 'farm') {
          const farm = this.supplyChainContext?.farmDetails;
          const woolLines = this.supplyChainContext?.woolDetails;
          
          if (woolLines && woolLines.mulesingStatus) {
            return `The mulesing status for wool from ${farm?.name || 'this farm'} is: ${woolLines.mulesingStatus}.`;
          } else {
            // Fallback response
            if (farm && farm.name && farm.name.toLowerCase().includes('ironbark')) {
              return `Ironbark Station follows non-mulesed wool production practices. All wool from this farm is certified as Non Mulesed (NM).`;
            } else {
              return `This farm's wool is produced with ethical animal welfare practices. For specific mulesing status information, please refer to the National Wool Declaration for this farm.`;
            }
          }
        } else if (this.entityType === 'woolLine') {
          const woolLine = this.supplyChainContext?.woolDetails;
          if (woolLine && woolLine.mulesingStatus) {
            return `The mulesing status for this wool line is: ${woolLine.mulesingStatus}.`;
          }
        }
        
        return "I don't have specific mulesing status information for this entity.";
      }
      
      // Owner queries
      if (lowerQuery.includes('owner') || lowerQuery.includes('who owns')) {
        // Check if we're looking at a farm
        if (this.entityType === 'farm') {
          const farm = this.supplyChainContext?.farmDetails;
          if (farm && farm.owner) {
            return `${farm.name} is owned by ${farm.owner}.`;
          } else if (farm && farm.name) {
            // Check for specific farms we know about
            if (farm.name.toLowerCase().includes('ironbark')) {
              return `Ironbark Station is owned by the Nguyen Family Trust.`;
            } else if (farm.name.toLowerCase().includes('kookaburra')) {
              return `Kookaburra Plains is owned by Ash & Mia Robertson.`;
            } else if (farm.name.toLowerCase().includes('wattle')) {
              return `Wattle Creek is owned by the Johnson Family.`;
            }
            return `I don't have specific ownership information for ${farm.name}.`;
          }
        }
        return "I don't have ownership information for this entity.";
      }
      
      // Farm details queries
      if (lowerQuery.includes('tell me about') || lowerQuery.includes('information') || 
          lowerQuery.includes('details') || lowerQuery.includes('where') || 
          lowerQuery.includes('location') || lowerQuery.includes('pic') || 
          lowerQuery.includes('wool') || lowerQuery.includes('production')) {
        
        // Check if we're looking at a farm
        if (this.entityType === 'farm') {
          // Get the farm insight that would have been sent automatically before
          const farmInsight = this.generateEntityInsight();
          if (farmInsight && farmInsight.trim() !== '') {
            return farmInsight;
          }
        }
      }
      
      // Farm-related queries
      if (lowerQuery.includes('farm') || lowerQuery.includes('origin')) {
        // Get relevant farms based on the context
        let relevantFarms = [];
        
        if (this.entityType === 'farm') {
          // We're already looking at a farm
          const farm = this.supplyChainContext?.farmDetails;
          if (farm && farm.name) {
            return `You're currently viewing ${farm.name} farm ${farm.location ? `in ${farm.location}` : ''}. ${farm.pic ? `The Property Identification Code (PIC) is ${farm.pic}.` : ''} ${farm.sustainabilityScore ? `The farm has a sustainability score of ${farm.sustainabilityScore}/100.` : ''}`;
          }
        } else if (this.entityType === 'commercial') {
          // For commercial entities, mention connected farms
          if (this.supplyChainContext?.factoryDetails?.factoryName) {
            return `${this.supplyChainContext.factoryDetails.factoryName} processes wool from multiple Australian farms. The specific farms that supply this facility include Kookaburra Plains (NSWKP1020), Ironbark Station (NSWIB3344), and others based on the trace data.`;
          } else if (this.supplyChainContext?.auctionDetails?.saleCenter) {
            return `${this.supplyChainContext.auctionDetails.saleCenter} handles wool from various farms including Kookaburra Plains, Ironbark Station, and Wattle Creek farms. These farms supply high-quality wool that is sold through this auction center.`;
          } else if (this.supplyChainContext?.exportDetails?.portName) {
            return `${this.supplyChainContext.exportDetails.portName} handles wool exports from multiple Australian farms to international destinations. The wool passing through this port comes from farms such as Kookaburra Plains, Ironbark Station, and others in the Australian wool industry.`;
          }
        } else if (this.entityType === 'woolLine') {
          // For wool lines, mention the source farm
          const farm = this.supplyChainContext?.farmDetails;
          if (farm && farm.name) {
            return `This wool line originated from ${farm.name} farm ${farm.location ? `in ${farm.location}` : ''}. ${farm.pic ? `The Property Identification Code (PIC) is ${farm.pic}.` : ''}`;
          }
        }
        
        // Generic response if we can't determine specific farms
        return "Based on the trace data, this wool supply chain involves several farms including Kookaburra Plains (NSW), Ironbark Station (Central West), Wattle Creek (Central Tablelands), and Waratah Downs (Monaro). Each farm has unique sustainability practices and wool characteristics.";
      }
      
      // Entity-specific queries - these take precedence over general queries
      if (this.entityType === 'brand') {
        // Brand specific queries
        if (lowerQuery.includes('farm') || lowerQuery.includes('source') || lowerQuery.includes('origin')) {
          const brandName = this.supplyChainData?.name || 'This brand';
          return `${brandName} sources wool primarily from premium Australian farms including Kookaburra Plains in New England, Ironbark Station in the Central West, and Redbank Hill in the Central West region. These farms are selected for their high-quality wool production and commitment to sustainable farming practices. The wool is fully traceable from these farms through the entire supply chain.`;
        }
        
        if (lowerQuery.includes('product') || lowerQuery.includes('make') || lowerQuery.includes('sell')) {
          const brandName = this.supplyChainData?.name || 'This brand';
          if (brandName.toLowerCase().includes('icebreaker')) {
            return `Icebreaker specializes in merino wool outdoor and performance clothing. Their product range includes base layers, mid layers, outerwear, socks, and accessories. They're particularly known for their merino wool base layers that offer natural temperature regulation, odor resistance, and moisture management properties.`;
          } else if (brandName.toLowerCase().includes('uniqlo')) {
            return `Uniqlo offers a wide range of wool products as part of their apparel line. They're particularly known for their merino wool sweaters, cardigans, and their HEATTECH thermal underwear which incorporates wool blends. Uniqlo focuses on creating everyday essentials with quality materials at accessible price points.`;
          } else {
            return `${brandName} produces a range of wool products including apparel, accessories, and home textiles. Their products emphasize the natural benefits of wool: temperature regulation, moisture management, odor resistance, and sustainability.`;
          }
        }
        
        if (lowerQuery.includes('sustain') || lowerQuery.includes('environment') || lowerQuery.includes('ethical')) {
          const brandName = this.supplyChainData?.name || 'This brand';
          if (brandName.toLowerCase().includes('icebreaker')) {
            return `Icebreaker has strong sustainability commitments, including sourcing wool from ZQ-certified farms that meet strict standards for animal welfare, environmental care, and social responsibility. They publish a transparency report detailing their supply chain practices and have goals to eliminate synthetic materials from their products. They're working toward full traceability from farm to finished product.`;
          } else if (brandName.toLowerCase().includes('uniqlo')) {
            return `Uniqlo has sustainability initiatives including their commitment to responsible wool sourcing. They're working with the Responsible Wool Standard (RWS) to ensure their wool comes from farms with high animal welfare standards and sustainable land management practices. Their parent company Fast Retailing has published sustainability targets including reducing environmental impact across their supply chain.`;
          } else {
            return `${brandName} is committed to sustainable and ethical wool sourcing. They work with certified farms that maintain high standards for animal welfare and environmental stewardship. Their supply chain emphasizes traceability and responsible production practices.`;
          }
        }
      }
      
      if (this.entityType === 'government_agency') {
        // Government agency specific queries
        if (lowerQuery.includes('role') || lowerQuery.includes('do') || lowerQuery.includes('function')) {
          const agencyName = this.supplyChainData?.name || 'Australian Wool Exchange';
          return `${agencyName} (AWEX) plays several critical roles in the Australian wool industry: 1) It establishes and maintains wool quality standards and specifications, 2) It manages the National Wool Declaration (NWD) program for mulesing status declarations, 3) It provides market reporting and price indicators including the Eastern Market Indicator (EMI), 4) It administers the wool classer registration program, ensuring professional standards, and 5) It facilitates the auction selling system that forms the primary wool marketing channel in Australia.`;
        }
        
        if (lowerQuery.includes('market') || lowerQuery.includes('price') || lowerQuery.includes('indicator')) {
          return `Australian Wool Exchange (AWEX) publishes key market indicators including the Eastern Market Indicator (EMI), which is the benchmark indicator for Australian wool prices. The EMI is calculated based on prices achieved at auctions in Sydney, Melbourne, and Fremantle. AWEX also provides detailed market reports breaking down prices by micron categories, allowing the industry to track price trends across different wool types. These indicators are essential for price transparency and market efficiency.`;
        }
        
        if (lowerQuery.includes('classer') || lowerQuery.includes('registration')) {
          return `Australian Wool Exchange (AWEX) manages the wool classer registration program, which ensures that wool classers meet professional standards. Registered classers receive a stencil number (e.g., AWEX990012) that must be applied to all wool they class. AWEX provides training, sets examination standards, and conducts audits to maintain classing quality. Currently, there are approximately 3,000 registered wool classers in Australia, including Jordan Blake (AWEX880045) and Taylor Quinn (AWEX990012) who are active in the system.`;
        }
        
        if (lowerQuery.includes('wool line') || lowerQuery.includes('recent') || lowerQuery.includes('latest') || 
            (lowerQuery.includes('wool') && lowerQuery.includes('through'))) {
          return `The most recent wool lines processed through the Australian Wool Exchange auction system include RED-25-027 from Redbank Hill (sold on October 18, 2025), KOO-25-003 from Kookaburra Plains (sold on October 18, 2025), and WAT-25-021 from Wattle Creek (sold on October 18, 2025). In total, AWEX facilitated the sale of approximately 45,000 bales across all Australian auction centers in the past week, with an average price of 1,105 cents per kg clean.`;
        }
        
        if (lowerQuery.includes('volume') || lowerQuery.includes('sale') || lowerQuery.includes('auction')) {
          return `Australian Wool Exchange (AWEX) facilitates the sale of approximately 1.5 million bales of wool annually through its auction system. The most recent weekly sale saw 45,000 bales offered across all centers with a clearance rate of 91%. The Sydney Wool Auction Center handled 15,000 bales, Melbourne handled 22,000 bales, and Fremantle handled 8,000 bales. The Eastern Market Indicator closed at 1,105 cents per kg clean, up 5 cents from the previous week.`;
        }
      }
      
      if (this.entityType === 'certification_body') {
        // Certification body specific queries
        if (lowerQuery.includes('standard') || lowerQuery.includes('certif') || lowerQuery.includes('require')) {
          const certName = this.supplyChainData?.name || 'SustainaWool Integrity Scheme';
          return `${certName} certification requires farms to meet specific standards in five key areas: 1) Animal welfare and handling practices, 2) Chemical use and environmental management, 3) Social responsibility and workplace conditions, 4) Traceability and record-keeping, and 5) Sustainable farm management practices. Farms undergo initial certification audits and then regular verification audits to maintain their certification status.`;
        }
        
        if (lowerQuery.includes('farm') || lowerQuery.includes('certif')) {
          return `SustainaWool Integrity Scheme currently has over 1,000 certified farms across Australia. Notable certified farms in the system include Kookaburra Plains (NSWKP1020), Redbank Hill (NSWRH2468), and Ironbark Station (NSWIB3344). These farms have demonstrated compliance with the scheme's standards for animal welfare, environmental stewardship, and sustainable farming practices.`;
        }
        
        if (lowerQuery.includes('benefit') || lowerQuery.includes('advantage')) {
          return `SustainaWool certification provides several benefits: 1) Market access to brands and retailers requiring certified wool, 2) Potential price premiums for certified wool at auction, 3) Recognition for sustainable farming practices, 4) Access to the SustainaWool traceability system, allowing wool to be tracked through the supply chain, and 5) Participation in an internationally recognized assurance program that demonstrates commitment to responsible wool production.`;
        }
      }
      
      if (this.entityType === 'auction_house') {
        // Auction house specific queries
        if (lowerQuery.includes('recent') || lowerQuery.includes('latest') || lowerQuery.includes('sale')) {
          const auctionName = this.supplyChainData?.name || 'Sydney Wool Auction Center';
          return `${auctionName}'s most recent sale was held on October 18, 2025, with 235 lots offered and a clearance rate of 92%. The top price achieved was 1,450 cents per kg for a 17.5 micron superfine merino lot from Kookaburra Plains. The average price across all lots was 1,120 cents per kg, which is 3.5% higher than the previous week's sale.`;
        }
        
        if (lowerQuery.includes('process') || lowerQuery.includes('how') || lowerQuery.includes('work')) {
          return `Sydney Wool Auction Center operates through an open-cry auction system. The process works as follows: 1) Wool lots are delivered to the auction center and displayed for inspection, 2) Test certificates from AWTA are made available to potential buyers, 3) Brokers prepare catalog entries with lot details and test results, 4) On auction day, buyers bid on lots in an open competitive environment, 5) The highest bidder secures the lot, and 6) After the sale, wool is prepared for shipping to the buyer's specified destination.`;
        }
        
        if (lowerQuery.includes('volume') || lowerQuery.includes('handle') || lowerQuery.includes('throughput')) {
          return `Sydney Wool Auction Center handles approximately 25,000 bales per month, representing about 30% of Australia's wool auction volume. The center operates weekly sales during the main season (August to June) and fortnightly sales during the slower winter period. Major wool brokers operating through this center include Southern Cross Wool Brokers and Pacific Plains Wool Brokers, who together represent over 500 wool producers.`;
        }
      }
      
      if (this.entityType === 'testing_lab') {
        // Testing lab specific queries
        if (lowerQuery.includes('recent') || lowerQuery.includes('latest') || lowerQuery.includes('last')) {
          return `The most recent wool lines tested by ${this.supplyChainData?.name || 'Australian Wool Testing Authority'} include RED-25-027 from Redbank Hill (tested on October 15, 2025), KOO-25-003 from Kookaburra Plains (tested on October 12, 2025), and SNO-25-030 from Snowy View (tested on October 10, 2025). Each of these wool lines received comprehensive testing for micron, yield, staple length, and vegetable matter content.`;
        }
        
        if (lowerQuery.includes('test') || lowerQuery.includes('certif')) {
          return `${this.supplyChainData?.name || 'Australian Wool Testing Authority'} issues test certificates that include detailed measurements of wool characteristics. Each certificate includes the wool's micron (fiber diameter), yield percentage, staple length and strength, position of break, vegetable matter content, and color measurements. These certificates are essential for determining wool value at auction and ensuring processors receive wool that meets their specifications.`;
        }
        
        if (lowerQuery.includes('process') || lowerQuery.includes('how')) {
          return `${this.supplyChainData?.name || 'Australian Wool Testing Authority'} follows a rigorous testing process: 1) Core samples are taken from wool bales, 2) Samples are conditioned in a controlled environment, 3) Fiber diameter is measured using lasers (OFDA) or airflow methods, 4) Yield is determined through washing and drying processes, 5) Staple measurements are conducted manually or with specialized equipment, 6) Results are verified and certified, 7) Test certificates are issued to wool owners and made available to potential buyers.`;
        }
      }
      
      // Wool quality and wool line queries
      if (lowerQuery.includes('wool') || lowerQuery.includes('quality') || lowerQuery.includes('micron')) {
        // For testing labs, provide information about their testing capabilities
        if (this.entityType === 'testing_lab') {
          return `${this.supplyChainData?.name || 'This testing laboratory'} tests wool for key quality metrics including micron (fiber diameter), yield percentage, staple length and strength, vegetable matter content, and color. The average micron range for Australian merino wool is 17-22μm, with finer wool (lower micron) generally commanding higher prices. Yield typically ranges from 65-75%, representing the clean wool content after processing.`;
        }
        
        // For wool line queries that aren't handled by entity-specific handlers
        if ((lowerQuery.includes('wool line') || lowerQuery.includes('recent') || lowerQuery.includes('latest') || 
             lowerQuery.includes('leave') || lowerQuery.includes('from')) && 
            !lowerQuery.includes('quality') && !lowerQuery.includes('micron')) {
          
          // Check if the query is asking about a specific farm by name
          for (const farmKeyword of ['snowy', 'redbank', 'kookaburra', 'wattle', 'ironbark', 'waratah', 'bluegum', 'banksia', 'eucalyptus', 'rivergum']) {
            if (lowerQuery.includes(farmKeyword)) {
              const farmData = this.getFarmSpecificWoolLines(farmKeyword);
              if (farmData) {
                const formattedName = farmData.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                return `The wool lines from ${formattedName} farm are ${farmData.lines[0]} (processed on October 10, 2025), ${farmData.lines[1]} (processed on October 5, 2025), and ${farmData.lines[2]} (processed on September 28, 2025). The most recent wool line is ${farmData.lines[0]}, which has been tested by AWTA and sold through the Sydney Wool Auction Center.`;
              }
            }
          }
          
          // Special case for farms - check if we're viewing a farm
          if (this.entityType === 'farm') {
            const farmName = this.supplyChainContext?.farmDetails?.name || this.supplyChainData?.name;
            
            // If we have a farm name, provide farm-specific wool lines
            if (farmName) {
              const farmData = this.getFarmSpecificWoolLines(farmName);
              if (farmData) {
                const formattedName = farmData.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                return `The wool lines from ${formattedName} farm are ${farmData.lines[0]} (processed on October 10, 2025), ${farmData.lines[1]} (processed on October 5, 2025), and ${farmData.lines[2]} (processed on September 28, 2025). The most recent wool line is ${farmData.lines[0]}, which has been tested by AWTA and sold through the Sydney Wool Auction Center.`;
              } else {
                // Generic response for other farms
                return `The most recent wool lines from ${farmName} have been processed through the supply chain with testing by AWTA and sale through auction centers. Each wool line is tracked from farm to final product, ensuring complete traceability.`;
              }
            }
          }
          
          // For other entity types, provide a contextual response based on entity type
          switch(this.entityType) {
            case 'brand':
              const brandName = this.supplyChainData?.name || 'This brand';
              return `${brandName} sources wool from multiple lines including RED-25-027 from Redbank Hill, KOO-25-003 from Kookaburra Plains, and SNO-25-030 from Snowy View. These wool lines were selected for their quality characteristics that meet ${brandName}'s specifications for fiber diameter, yield, and strength.`;
              
            case 'certification_body':
              return `Recent wool lines certified under the SustainaWool Integrity Scheme include RED-25-027, KOO-25-003, and IRO-25-005. These wool lines come from farms that have demonstrated compliance with the scheme's standards for animal welfare, environmental stewardship, and sustainable farming practices.`;
              
            case 'classer':
              const classerName = this.supplyChainData?.name || 'This wool classer';
              if (classerName.toLowerCase().includes('jordan')) {
                return `Jordan Blake has recently classed wool lines KOO-25-001, KOO-25-002, and IRO-25-004. Each wool line is carefully assessed for fiber characteristics and prepared to maximize its value at auction.`;
              } else if (classerName.toLowerCase().includes('taylor')) {
                return `Taylor Quinn has recently classed wool lines RED-25-025, RED-25-026, and WAT-25-019. Each wool line is carefully assessed and categorized based on its unique characteristics to ensure uniform lots for processing.`;
              } else {
                return `Recent wool lines classed by registered wool classers include RED-25-027, KOO-25-003, and WAT-25-021. Professional wool classers ensure each line is properly assessed and categorized to maximize its value.`;
              }
              
            case 'auction_house':
              return `Recent wool lines sold through this auction center include RED-25-027 from Redbank Hill (sold on October 18, 2025), KOO-25-003 from Kookaburra Plains (sold on October 18, 2025), and WAT-25-021 from Wattle Creek (sold on October 18, 2025). These lines achieved prices ranging from 1,050 to 1,450 cents per kg clean.`;
              
            default:
              return `Recent wool lines in the system include RED-25-027 from Redbank Hill, KOO-25-003 from Kookaburra Plains, and WAT-25-021 from Wattle Creek. Each wool line has its own unique characteristics and follows a traceable path through the supply chain.`;
          }
        }
        
        // For other wool quality queries, use the original response
        const wool = this.supplyChainContext?.woolDetails;
        if (wool) {
          return `This is ${wool.type || 'wool'} with a micron measurement of ${wool.micron || 'unknown'} μm ${wool.yield ? `and a yield of ${wool.yield}%` : ''}. ${wool.stapleLength ? `The staple length is ${wool.stapleLength}mm.` : ''} ${wool.mulesingStatus ? `Mulesing status: ${wool.mulesingStatus}.` : ''} ${wool.description ? `\n\nDescription: ${wool.description}` : ''}`;
        }
        return "I don't have detailed wool quality information for this batch.";
      }
      
      // Classer queries
      if (lowerQuery.includes('classer') || lowerQuery.includes('class')) {
        // For classer entity type
        if (this.entityType === 'classer') {
          const classerName = this.supplyChainData?.name || 'This wool classer';
          
          if (lowerQuery.includes('experience') || lowerQuery.includes('background')) {
            if (classerName.toLowerCase().includes('jordan blake')) {
              return `Jordan Blake has been a registered wool classer for 15 years, with AWEX registration number AWEX880045. Jordan specializes in superfine merino wool and has worked extensively in the New England and Central West regions of NSW. With a background in agricultural science, Jordan is known for exceptional attention to detail and consistency in classing standards.`;
            } else if (classerName.toLowerCase().includes('taylor quinn')) {
              return `Taylor Quinn has been a registered wool classer for 8 years, with AWEX registration number AWEX990012. Taylor has expertise in both merino and crossbred wool types and primarily works in the Riverina and Monaro regions. Taylor completed advanced classer training through TAFE NSW and is certified in both traditional and objective measurement-assisted classing techniques.`;
            } else {
              return `${classerName} is a registered wool classer with the Australian Wool Exchange (AWEX). Wool classers undergo specialized training and certification to assess and categorize wool based on its characteristics, ensuring consistent quality standards across the industry.`;
            }
          }
          
          if (lowerQuery.includes('farm') || lowerQuery.includes('work')) {
            if (classerName.toLowerCase().includes('jordan blake')) {
              return `Jordan Blake has worked with several farms in the system including Kookaburra Plains, Ironbark Station, and Bluegum Ridge. In the current season, Jordan has classed approximately 15 wool lines, including KOO-25-001, KOO-25-002, and IRO-25-004. Jordan's classing is particularly valued for consistency in preparing uniform wool lots.`;
            } else if (classerName.toLowerCase().includes('taylor quinn')) {
              return `Taylor Quinn has worked with Wattle Creek, Redbank Hill, and Waratah Downs farms this season. Taylor has classed approximately 12 wool lines including RED-25-025, RED-25-026, and WAT-25-019. Taylor is known for maximizing returns through careful attention to optimal lot preparation and separation.`;
            } else {
              return `${classerName} works with multiple farms in the wool growing regions of Australia, providing professional wool classing services during shearing. A skilled classer can significantly impact the value of a wool clip through proper preparation and lot building.`;
            }
          }
          
          if (lowerQuery.includes('role') || lowerQuery.includes('do')) {
            return `As a wool classer, ${classerName} is responsible for examining freshly shorn wool and categorizing it based on key characteristics including fiber diameter (micron), staple length, strength, color, and vegetable matter content. The classing process involves separating wool into uniform lots that will process similarly and command optimal prices. This includes identifying and removing contamination, managing the wool handling team, and completing documentation including the wool classer's specification that accompanies each lot to auction.`;
          }
          
          return `${classerName} is a registered wool classer who assesses and categorizes wool during shearing. Wool classers play a critical role in maximizing the value of the wool clip by creating uniform lots based on fiber characteristics.`;
        }
        
        // For other entity types
        const wool = this.supplyChainContext?.woolDetails;
        if (wool && wool.classer) {
          return `This wool was classed by ${wool.classer}${wool.classerId ? ` (ID: ${wool.classerId})` : ''}.`;
        }
        return "I don't have information about the wool classer for this batch.";
      }
      
      // Testing and certification queries
      if (lowerQuery.includes('test') || lowerQuery.includes('certificate') || lowerQuery.includes('awta')) {
        const test = this.supplyChainContext?.testingDetails;
        if (test && test.testCertId) {
          return `This wool batch was tested and certified under AWTA certificate ${test.testCertId}${test.testDate ? ` on ${test.testDate}` : ''}.`;
        }
        return "I don't have detailed testing information for this wool batch.";
      }
      
      // Auction and sale queries
      if (lowerQuery.includes('auction') || lowerQuery.includes('sale') || lowerQuery.includes('price')) {
        const auction = this.supplyChainContext?.auctionDetails;
        if (auction) {
          return `The wool was sold at ${auction.saleCenter || 'auction'}${auction.saleDate ? ` on ${auction.saleDate}` : ''}${auction.lotNo ? ` as lot number ${auction.lotNo}` : ''}${auction.price ? ` for ${auction.price} cents per kg greasy` : ''}.`;
        }
        return "I don't have detailed auction or sale information for this wool batch.";
      }
      
      // Export and shipping queries
      if (lowerQuery.includes('export') || lowerQuery.includes('ship') || lowerQuery.includes('consignment')) {
        const export_info = this.supplyChainContext?.exportDetails;
        if (export_info) {
          return `The wool was exported as part of consignment ${export_info.consignmentId || 'unknown'}${export_info.destination ? ` to ${export_info.destination}` : ''}${export_info.portId ? ` through port ${export_info.portId}` : ''}${export_info.vesselName ? ` on vessel ${export_info.vesselName}` : ''}.`;
        }
        return "I don't have detailed export information for this wool batch.";
      }
      
      // Manufacturing and factory queries
      if (lowerQuery.includes('factory') || lowerQuery.includes('manufactur') || lowerQuery.includes('process')) {
        const factory = this.supplyChainContext?.factoryDetails;
        if (factory) {
          return `The wool was processed at ${factory.factoryName || 'unknown factory'}${factory.factoryId ? ` (ID: ${factory.factoryId})` : ''}${factory.country ? ` in ${factory.country}` : ''}.`;
        }
        return "I don't have detailed manufacturing information for this wool batch.";
      }
      
      // Product and brand queries
      if (lowerQuery.includes('product') || lowerQuery.includes('brand') || lowerQuery.includes('fabric')) {
        const product = this.supplyChainContext?.productDetails;
        
        // Check if the query is specifically about which farms a brand sources from
        if (lowerQuery.includes('which farm') || 
            (lowerQuery.includes('brand') && lowerQuery.includes('farm')) ||
            (lowerQuery.includes('wool') && lowerQuery.includes('from'))) {
          
          if (this.entityType === 'commercial' && this.supplyChainContext?.factoryDetails?.factoryName) {
            return `The ${product?.brand || 'brands using wool processed at this facility'} source their wool from multiple Australian farms including Kookaburra Plains (NSW), Ironbark Station (Central West), and Wattle Creek (Central Tablelands). These farms are selected for their high-quality wool and sustainable farming practices.`;
          } else if (product && product.brand) {
            return `${product.brand} sources wool from several Australian farms including Kookaburra Plains, Ironbark Station, and Wattle Creek farms. These farms are selected for their high-quality wool and sustainable farming practices.`;
          } else {
            return `The brands in this supply chain source their wool from several Australian farms including Kookaburra Plains (NSW), Ironbark Station (Central West), and Wattle Creek (Central Tablelands). These farms are selected for their high-quality wool and sustainable farming practices.`;
          }
        }
        
        // Standard brand/product response
        if (product && (product.brand || product.fabricType)) {
          return `The wool was used to create ${product.fabricType || 'fabric'} for the ${product.brand || 'various brands'}${product.yarnCount ? ` with a yarn count of ${product.yarnCount}` : ''}. ${product.fabricWeight ? `The fabric weight is ${product.fabricWeight} GSM.` : ''}`;
        }
        
        // Generic response if we don't have specific product details
        if (this.entityType === 'commercial' && this.supplyChainContext?.factoryDetails?.factoryName) {
          return `${this.supplyChainContext.factoryDetails.factoryName} processes wool into various fabric types for multiple international clothing and textile brands. The specific products include fine merino fabrics for luxury apparel, performance textiles, and home furnishings.`;
        }
        
        return "The wool in this supply chain is processed into various products including luxury apparel fabrics, performance textiles, and home furnishings for multiple international brands.";
      }
      
      // Sustainability queries
      if (lowerQuery.includes('sustain') || lowerQuery.includes('environment') || lowerQuery.includes('carbon') || lowerQuery.includes('water')) {
        const sustainability = this.supplyChainContext?.sustainabilityDetails;
        if (sustainability) {
          let response = "Sustainability information: ";
          if (sustainability.carbonFootprint) {
            response += `Carbon footprint: ${sustainability.carbonFootprint} kg CO₂. `;
          }
          if (sustainability.waterUsage) {
            response += `Water usage: ${sustainability.waterUsage} liters. `;
          }
          if (sustainability.certifications) {
            const certs = [];
            if (sustainability.certifications.rws) certs.push("RWS (Responsible Wool Standard)");
            if (sustainability.certifications.zq) certs.push("ZQ Merino");
            if (sustainability.certifications.gots) certs.push("GOTS (Global Organic Textile Standard)");
            
            if (certs.length > 0) {
              response += `Certifications: ${certs.join(", ")}.`;
            }
          }
          return response;
        }
        return "I don't have detailed sustainability information for this wool batch.";
      }
      
      // Traceability and supply chain queries
      if (lowerQuery.includes('trace') || lowerQuery.includes('supply chain') || lowerQuery.includes('journey')) {
        return `This wool batch can be traced from ${this.supplyChainContext?.farmDetails?.name || 'the farm'} to ${this.supplyChainContext?.factoryDetails?.factoryName || 'the factory'}. The complete journey includes shearing, testing by AWTA (${this.supplyChainContext?.testingDetails?.testCertId || 'certificate'}), auction sale (${this.supplyChainContext?.auctionDetails?.saleCenter || 'auction center'}), export (${this.supplyChainContext?.exportDetails?.consignmentId || 'consignment'}), and manufacturing into ${this.supplyChainContext?.productDetails?.fabricType || 'fabric'}.`;
      }
      
      // Tab-specific responses
      if (this.activeTab === 'supply-chain') {
        if (lowerQuery.includes('explain') || lowerQuery.includes('show') || lowerQuery.includes('what')) {
          return "The supply chain view shows the journey of the wool from farm to retail. Each card represents a stage in the process. You can toggle the filters at the top to see different aspects like compliance, sustainability, regulatory checkpoints, and risks.";
        }
      } else if (this.activeTab === 'connections-graph') {
        if (lowerQuery.includes('explain') || lowerQuery.includes('show') || lowerQuery.includes('what')) {
          return "The connections graph visualizes the relationships between different entities in the wool supply chain. Nodes represent farms, testing facilities, auctions, factories, etc. The lines show how these entities are connected. You can click on any node to see more details.";
        }
      } else if (this.activeTab === 'files') {
        if (lowerQuery.includes('explain') || lowerQuery.includes('show') || lowerQuery.includes('what')) {
          return "The files tab shows all documents related to this wool batch, including certificates, reports, images, and other documentation. You can filter by file type or search for specific files.";
        }
      }
      
      // General queries about the data
      if (lowerQuery.includes('data') || lowerQuery.includes('information')) {
        return "This interface provides comprehensive traceability data for wool from farm to retail. You can see information about the farm of origin, wool quality testing, auction sale, export logistics, manufacturing, and final products. What specific aspect would you like to know more about?";
      }
      
      // Help queries
      if (lowerQuery.includes('help') || lowerQuery.includes('what can you do')) {
        return "I can help you understand the wool supply chain data. You can ask me about:<br>- Farm origin and details<br>- Wool quality and specifications<br>- Testing and certification<br>- Auction and sale information<br>- Export and shipping details<br>- Manufacturing and processing<br>- Product and brand information<br>- Sustainability metrics<br>- Supply chain traceability<br>Just ask me what you'd like to know!";
      }
      
      // Shipping and freight company queries
      if (lowerQuery.includes('shipping') || lowerQuery.includes('freight') || lowerQuery.includes('vessel') || 
          lowerQuery.includes('consignment') || lowerQuery.includes('container')) {
        const export_info = this.supplyChainContext?.exportDetails;
        
        // Check if the query is about wool lines handled by a shipping company
        if ((lowerQuery.includes('wool line') || lowerQuery.includes('wool batch') || lowerQuery.includes('involved in')) && 
            export_info?.freightCompany) {
          return `${export_info.freightCompany} handles multiple wool consignments including lines from Kookaburra Plains (KOO-25-001, KOO-25-002), Redbank Hill (RED-25-027), and Ironbark Station (IRO-25-015). These wool lines are exported through ${export_info.portName || 'Australian ports'} to international destinations.`;
        }
        
        if (export_info?.freightCompany) {
          let response = `${export_info.freightCompany} is a freight company that specializes in wool exports. `;
          
          if (export_info.vesselName) {
            response += `They operate vessels such as ${export_info.vesselName}. `;
          }
          
          if (export_info.portName) {
            response += `They primarily operate through ${export_info.portName}. `;
          }
          
          if (export_info.consignmentId) {
            response += `Current consignment ID: ${export_info.consignmentId}. `;
          }
          
          return response;
        }
        
        return "Shipping companies play a critical role in the wool supply chain, transporting wool from Australian ports to international markets. Major freight companies include Sunrise Global Freight, Pacific Shipping Lines, and Southern Cross Maritime.";
      }
      
      // Default response
      return "I'm here to help you understand the wool supply chain data. You can ask me about the farm origin, wool quality, testing, auction details, export information, manufacturing, or sustainability aspects. What would you like to know?";
    },
    
        /**
     * Creates a properly formatted HTML link for the carbon report
     * @param {string} url - The URL to link to
     * @param {string} text - The link text
     * @returns {string} - The formatted HTML link
     */
    createCarbonReportLink(url, text = 'Click here to open the Carbon Report in a new tab') {
      return `<a href="${url}" target="_blank" class="carbon-report-link" style="color: #3A895F !important; text-decoration: underline !important; font-weight: 500;">${text}</a>`;
    },
    
    async handleCarbonReportRequest(farmPIC) {
      try {
        // Fetch farm carbon data from the API
        // Use the full URL with port to ensure correct routing
        // Vue app runs on port 5173, Laravel API runs on port 8000
        const apiUrl = window.location.hostname === 'localhost' ? 
          `http://localhost:8000/api/carbon-report/farm/${farmPIC}` : 
          `/api/carbon-report/farm/${farmPIC}`;
        
        // Add credentials to ensure cookies are sent if needed
        const fetchOptions = {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        };
        
        console.log('AI Assistant: Fetching carbon data from:', apiUrl);
        const response = await fetch(apiUrl, fetchOptions);
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('AI Assistant: Carbon data received:', data);
        
        // Check if farmData exists and has the expected structure
        if (!data.farmData) {
          console.error('AI Assistant: farmData is missing in the API response');
          data.farmData = {};
        } else {
          console.log('AI Assistant: farmData structure:', {
            name: data.farmData.name,
            FarmName: data.farmData.FarmName,
            farm_name: data.farmData.farm_name,
            keys: Object.keys(data.farmData)
          });
        }
        
        // Check if woolLines exists
        if (!data.woolLines || !Array.isArray(data.woolLines)) {
          console.error('AI Assistant: woolLines is missing or not an array in the API response');
          data.woolLines = [];
        } else {
          console.log('AI Assistant: woolLines count:', data.woolLines.length);
        }
        
        // Check the structure of the API response
        // The API returns data.farm but our PDF service expects certain field names
        let farmData = {};
        
        // Check if we have data.farm or data.farmData
        if (data.farm) {
          farmData = { ...data.farm };
          console.log('AI Assistant: Using data.farm');
        } else if (data.farmData) {
          farmData = { ...data.farmData };
          console.log('AI Assistant: Using data.farmData');
        } else {
          console.log('AI Assistant: No farm data found in API response');
        }
        
        const woolLines = data.woolLines || [];
        
        // Log the raw farm data before mapping
        console.log('AI Assistant: Raw farm data:', JSON.stringify(farmData));
        
        // Map the farm data to the expected format
        if (farmData) {
          // Create lowercase keys for case-insensitive access
          const lowerCaseKeys = {};
          Object.keys(farmData).forEach(key => {
            lowerCaseKeys[key.toLowerCase()] = key;
          });
          
          console.log('AI Assistant: Available keys (lowercase):', Object.keys(lowerCaseKeys));
          
          // Map farm name
          if (!farmData.name) {
            if (lowerCaseKeys['farmname'] && farmData[lowerCaseKeys['farmname']]) {
              farmData.name = farmData[lowerCaseKeys['farmname']];
              console.log('AI Assistant: Set name to:', farmData.name);
            }
          }
          
          // Map PIC
          if (!farmData.PIC && lowerCaseKeys['pic']) {
            farmData.PIC = farmData[lowerCaseKeys['pic']];
            console.log('AI Assistant: Set PIC to:', farmData.PIC);
          }
          
          // Map location
          if (!farmData.location) {
            if (lowerCaseKeys['region']) {
              const region = farmData[lowerCaseKeys['region']];
              const lga = lowerCaseKeys['lga'] ? farmData[lowerCaseKeys['lga']] : null;
              farmData.location = lga ? `${region}, ${lga}` : region;
              console.log('AI Assistant: Set location to:', farmData.location);
            }
          }
          
          // Map owner
          if (!farmData.owner && lowerCaseKeys['ownername']) {
            farmData.owner = farmData[lowerCaseKeys['ownername']];
            console.log('AI Assistant: Set owner to:', farmData.owner);
          }
          
          // Map carbon reporting status
          if (!farmData.CarbonReportingStatus && lowerCaseKeys['carbonreportingstatus']) {
            farmData.CarbonReportingStatus = farmData[lowerCaseKeys['carbonreportingstatus']];
            console.log('AI Assistant: Set CarbonReportingStatus to:', farmData.CarbonReportingStatus);
          }
          
          // Map GHG scopes
          if (!farmData.GHGScopesCovered && lowerCaseKeys['ghgscopescovered']) {
            farmData.GHGScopesCovered = farmData[lowerCaseKeys['ghgscopescovered']];
            console.log('AI Assistant: Set GHGScopesCovered to:', farmData.GHGScopesCovered);
          }
          
          // Map certification
          if (!farmData.certification) {
            if (lowerCaseKeys['sustainawool'] && farmData[lowerCaseKeys['sustainawool']]) {
              farmData.certification = 'SustainaWool Certified';
              console.log('AI Assistant: Set certification to:', farmData.certification);
            }
          }
          
          // Log the mapped farm data fields that will be used in the PDF
          console.log('AI Assistant: Mapped farm data fields:', {
            name: farmData.name,
            PIC: farmData.PIC,
            location: farmData.location,
            owner: farmData.owner,
            CarbonReportingStatus: farmData.CarbonReportingStatus,
            GHGScopesCovered: farmData.GHGScopesCovered,
            Scope3_Compliant: farmData.Scope3_Compliant,
            certification: farmData.certification
          });
        }
        
        console.log('AI Assistant: Using farm data:', farmData);
        console.log('AI Assistant: Using wool lines:', woolLines.length);
        
        // Generate the PDF using the PdfService
        // PdfService is exported as an instance, not a class
        const pdfUrl = await PdfService.generateCarbonReport(farmData, woolLines);
        
        console.log('AI Assistant: PDF generated, URL:', pdfUrl);
        
        // Get the farm name for the message
        const farmName = farmData?.name || farmData?.FarmName || this.supplyChainContext?.farmDetails?.name || 'Farm';
        
        // Create the link text and HTML
        const linkText = 'Click here to open the Carbon Report in a new tab';
        // Use createCarbonReportLink method for consistency
        const linkHTML = this.createCarbonReportLink(pdfUrl, linkText);
        
        const successMessage = {
          id: Date.now(),
          type: 'assistant',
          content: `I've generated a Carbon Report for ${farmName} with details on carbon reporting status, sustainability practices, and associated wool lines.<br><br>${linkHTML}`,
          timestamp: new Date()
        };
        
        // Simply add the success message to the chat
        this.messages.push(successMessage);
        this.isProcessing = false;
        
        return true;
      } catch (error) {
        console.error('Error generating carbon report:', error);
        
        // Show error message with more details
        const errorMessage = {
          id: Date.now(),
          type: 'assistant',
          content: `Sorry, I encountered an error while generating the carbon report: ${error.message}. Please check the console for more details or try again later.`,
          timestamp: new Date()
        };
        
        // Add the error message to the chat
        this.messages.push(errorMessage);
        this.isProcessing = false;
        
        return false;
      }
    },
    
    formatMessage(message) {
      // If the message already contains HTML (like our carbon report link), return it as is
      if (message.includes('<a href=') || message.includes('<br>')) {
        return message;
      }
      
      // Otherwise, convert URLs to clickable links and handle line breaks
      return message
        .replace(/https?:\/\/[^\s]+/g, url => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`)
        .replace(/\n/g, '<br>');
    },
    
    getFarmSpecificWoolLines(farmName) {
      // Return wool lines specific to a farm
      const farmData = [
        { name: 'snowy view', prefix: 'SNO', location: 'Monaro', pic: 'NSWSV1357', 
          lines: ['SNO-25-030', 'SNO-25-029', 'SNO-25-028'] },
        { name: 'redbank hill', prefix: 'RED', location: 'Central West', pic: 'NSWRH2468', 
          lines: ['RED-25-027', 'RED-25-026', 'RED-25-025'] },
        { name: 'kookaburra plains', prefix: 'KOO', location: 'New England', pic: 'NSWKP1020', 
          lines: ['KOO-25-003', 'KOO-25-002', 'KOO-25-001'] },
        { name: 'wattle creek', prefix: 'WAT', location: 'Central Tablelands', pic: 'NSWWC6789', 
          lines: ['WAT-25-021', 'WAT-25-020', 'WAT-25-019'] },
        { name: 'ironbark station', prefix: 'IRO', location: 'Central West', pic: 'NSWIB3344', 
          lines: ['IRO-25-006', 'IRO-25-005', 'IRO-25-004'] },
        { name: 'waratah downs', prefix: 'WAR', location: 'Monaro', pic: 'NSWWD7788', 
          lines: ['WAR-25-012', 'WAR-25-011', 'WAR-25-010'] },
        { name: 'bluegum ridge', prefix: 'BLU', location: 'Northern Tablelands', pic: 'NSWBR9012', 
          lines: ['BLU-25-015', 'BLU-25-014', 'BLU-25-013'] },
        { name: 'banksia vale', prefix: 'BAN', location: 'Riverina', pic: 'NSWBV5566', 
          lines: ['BAN-25-009', 'BAN-25-008', 'BAN-25-007'] },
        { name: 'eucalyptus park', prefix: 'EUC', location: 'Hunter', pic: 'NSWEP2345', 
          lines: ['EUC-25-018', 'EUC-25-017', 'EUC-25-016'] },
        { name: 'rivergum plains', prefix: 'RIV', location: 'Riverina', pic: 'NSWRP4321', 
          lines: ['RIV-25-024', 'RIV-25-023', 'RIV-25-022'] }
      ];
      
      // Normalize farm name for comparison
      const normalizedName = farmName.toLowerCase();
      
      // Find matching farm
      for (const farm of farmData) {
        if (farm.name.includes(normalizedName) || 
            normalizedName.includes(farm.name) || 
            normalizedName.includes(farm.prefix.toLowerCase())) {
          return farm;
        }
      }
      
      return null;
    },
    
    formatTimestamp(timestamp) {
      // Format timestamp to show only hours and minutes
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    
    scrollToBottom() {
      const container = this.$refs.chatMessages;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Store the file in the component's data
      this.uploadedFile = file;
      
      // Update the input placeholder to show the file name
      this.userInput = `I'm uploading ${file.name}. `;
      
      // Focus on the input field so the user can add a message
      this.$nextTick(() => {
        const inputEl = document.querySelector('.ai-input');
        if (inputEl) {
          inputEl.focus();
        }
      });
    },
    
    async processAuditReport(file) {
      // Create a response message about processing the audit report
      const aiMessage = {
        id: Date.now(),
        type: 'assistant',
        content: `I'm analyzing the audit report you uploaded (${file.name}). Let me check which items have been addressed...`,
        timestamp: new Date()
      };
      
      this.messages.push(aiMessage);
      
      // Read the file content to provide accurate feedback
      const reader = new FileReader();
      
      try {
        // Read the file content as text
        const content = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsText(file);
        });
        
        // Check for compliance status in each section
        const biosecurityCompliant = content.includes('BIOSECURITY RECORDS') && 
                                    content.includes('Status: COMPLIANT');
        
        const animalTreatmentsCompliant = content.includes('ANIMAL TREATMENTS & MEDICATION RECORDS') && 
                                         content.includes('Status: COMPLIANT');
        
        const nlisMovementsCompliant = content.includes('NLIS / ANIMAL MOVEMENTS') && 
                                      content.includes('Status: COMPLIANT');
        
        const feedCroppingCompliant = content.includes('FEED & CROPPING RECORDS') && 
                                     content.includes('Status: COMPLIANT');
        
        // Emit an event to update the checkboxes in the PreAuditGuidance component
        console.log('AIAssistantPanel: Emitting process-audit-report event with file:', file);
        this.$emit('process-audit-report', file);
        
        // Build a response based on the actual content
        let responseContent = `I've analyzed the audit report and updated the Pre-Audit Guidance checklist.<br><br>`;
        
        // Add compliant items
        let compliantItems = [];
        if (biosecurityCompliant) {
          compliantItems.push('<strong>Biosecurity Records</strong>: Visitor logs are now complete with entries for the missing period.');
        }
        if (animalTreatmentsCompliant) {
          compliantItems.push('<strong>Animal Treatments & Medication Records</strong>: The report includes the missing records from Dec 2024 onwards.');
        }
        if (nlisMovementsCompliant) {
          compliantItems.push('<strong>NLIS / Animal Movements</strong>: The consignment records have been verified.');
        }
        if (feedCroppingCompliant) {
          compliantItems.push('<strong>Feed & Cropping Records</strong>: Crop input data and supplier invoices are now properly documented.');
        }
        
        if (compliantItems.length > 0) {
          responseContent += `The following items have been marked as completed based on the report:<br>`;
          compliantItems.forEach(item => {
            responseContent += `- ${item}<br>`;
          });
          responseContent += `<br>`;
        }
        
        // Add non-compliant items
        let nonCompliantItems = [];
        if (!biosecurityCompliant) {
          nonCompliantItems.push('Biosecurity Records: Visitor logs for the May-August 2025 period are still missing.');
        }
        if (!animalTreatmentsCompliant) {
          nonCompliantItems.push('Animal Treatments & Medication Records: Records after Dec 2024 are still missing.');
        }
        if (!nlisMovementsCompliant) {
          nonCompliantItems.push('NLIS / Animal Movements: The two outgoing consignments still show unconfirmed receipt PICs.');
        }
        if (!feedCroppingCompliant) {
          nonCompliantItems.push('Feed & Cropping Records: Crop input data for 2025 winter season is still missing.');
        }
        
        if (nonCompliantItems.length > 0) {
          responseContent += `The following items still need attention:<br>`;
          nonCompliantItems.forEach(item => {
            responseContent += `- ${item}<br>`;
          });
        }
        
        // Send a follow-up message with the results
        const resultsMessage = {
          id: Date.now(),
          type: 'assistant',
          content: responseContent,
          timestamp: new Date()
        };
        
        this.messages.push(resultsMessage);
      } catch (error) {
        console.error('Error processing audit report:', error);
        
        // Send an error message
        const errorMessage = {
          id: Date.now(),
          type: 'assistant',
          content: `I encountered an error while analyzing the audit report. Please make sure the file is in a readable text format.`,
          timestamp: new Date()
        };
        
        this.messages.push(errorMessage);
      } finally {
        this.isProcessing = false;
      }
    }
  }
};
</script>

<style scoped>
.ai-assistant-panel {
  width: 400px;
  background-color: white;
  border-left: 1px solid #e5e5e5;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
  height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  z-index: 100;
}

.ai-panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  background: white;
}

.ai-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #3D8B5F;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.ai-title {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin: 0;
}

.ai-title {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.ai-panel-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.ai-chat-messages {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ai-message-wrapper {
  display: flex;
  flex-direction: column;
}

.ai-message {
  max-width: 90%;
}

.ai-message-assistant {
  align-self: flex-start;
}

.ai-message-user {
  align-self: flex-end;
}

.ai-message-content {
  background: #ffffff;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.ai-message-assistant .ai-message-content {
  border-left: 3px solid #3D8B5F;
}

.ai-message-user .ai-message-content {
  background: #3D8B5F;
  color: white;
  border: 1px solid #3D8B5F;
}

.ai-message-content p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #374151;
}

.ai-message-user .ai-message-content p {
  color: white;
}

.ai-message-timestamp {
  font-size: 10px;
  color: #9ca3af;
  margin-top: 4px;
  text-align: right;
}

.ai-message-user .ai-message-timestamp {
  color: rgba(255, 255, 255, 0.7);
}

.ai-thinking {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  min-height: 24px;
}

.ai-thinking .dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #3D8B5F;
  margin: 0 2px;
  opacity: 0.6;
  animation: dot-pulse 1.5s infinite ease-in-out;
}

.ai-thinking .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.ai-thinking .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dot-pulse {
  0%, 100% { transform: scale(0.8); opacity: 0.6; }
  50% { transform: scale(1.2); opacity: 1; }
}

.ai-input-container {
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  gap: 8px;
  align-items: center;
}

.ai-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
}

.ai-input:focus {
  border-color: #3D8B5F;
}

.ai-send-button {
  min-width: 32px;
  height: 32px;
  background: #3D8B5F;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-send-button:hover:not(:disabled) {
  background: #2D6A47;
}

.ai-send-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.ai-file-button {
  min-width: 32px;
  height: 32px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #3D8B5F;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.ai-file-button:hover {
  background: #e5e7eb;
  border-color: #3D8B5F;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .ai-assistant-panel {
    width: 350px;
  }
}

@media (max-width: 992px) {
  .ai-assistant-panel {
    width: 320px;
  }
}

@media (max-width: 768px) {
  .ai-assistant-panel {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e5e5e5;
  }
}
</style>
